import {
  BrowserLocationState,
  useBrowserLocation,
} from "@folio-2026/browser-location"
import { debugAtom, debugStore } from "@folio-2026/experience"
import Camera from "@folio-2026/section/camera"
import { EVENTS } from "@folio-2026/shared"
import gsap from "gsap"
import * as THREE from "three/webgpu"

import {
  cameraFovPostVertigo,
  cameraFovPreVertigo,
  cameraMovements,
} from "./const"

export default class LoadingSectionCamera extends Camera {
  camMovements: {
    positionX: number
    positionY: number
    positionZ: number
    lookAtX: number
    lookAtY: number
    lookAtZ: number
  }

  // Mouse interaction settings
  private isEntered: boolean

  private introAnimation: gsap.core.Tween
  private aboutAnimation: gsap.core.Tween
  private introExitingAnimation: gsap.core.Tween

  private vertigoAnimation: gsap.core.Timeline

  private isOrbitControls: boolean = false

  constructor({ scene }: { scene: THREE.Scene }) {
    super({ scene })
    const pathname = useBrowserLocation.getState().pathname

    const camMovLoadingPreVertigo = cameraMovements.get(
      this.sizes.isMobile
        ? "loading-pre-vertigo-mobile"
        : "loading-pre-vertigo",
    )
    const camMovLoading = cameraMovements.get(
      this.sizes.isMobile ? "loading-mobile" : "loading",
    )
    const camMovAbout = cameraMovements.get("about")

    if (pathname === "/") {
      this.camMovements = { ...camMovLoadingPreVertigo }
      this.isEntered = false
    } else if (pathname === "/about") {
      this.camMovements = { ...camMovAbout }
      this.isEntered = true
    } else {
      // Project URL or other path: use loading (entered) position so the loader camera matches project view
      this.camMovements = { ...camMovLoading }
      this.isEntered = true
    }

    this.introAnimation = this.createAnimation(
      this.sizes.isMobile ? "loading-mobile" : "loading",
    )
    this.aboutAnimation = this.createAnimation("about")
    this.introExitingAnimation = this.createAnimation("loading-exiting")

    this.vertigoAnimation = this.createVertigoAnimation()

    // Set FOV after creating vertigo animation to prevent .fromTo() from overriding it
    this.camera.fov =
      pathname === "/" ? cameraFovPreVertigo : cameraFovPostVertigo
    this.camera.updateProjectionMatrix()

    this.setDebug()

    this.listenEvents()
  }

  listenEvents() {
    // Action that come from the browser location
    useBrowserLocation.subscribe(this.actionsFromBrowserLocation.bind(this))

    this.experience.on(
      EVENTS.Loader.CurtainStartedRemoving,
      this.removeCurtainAnimation.bind(this),
    )

    this.experience.on(EVENTS.Loader.Entered, () => {
      this.isEntered = true
    })

    // Loader section exiting (Works entering)
    this.experience.on(EVENTS.Loader.Exiting, this.leaveAnimation.bind(this))
  }

  actionsFromBrowserLocation(
    state: BrowserLocationState,
    prevState?: BrowserLocationState,
  ) {
    if (state.pathname === "/about" && prevState?.pathname !== "/about") {
      this.isEntered = true
      // Kill any active tweens on positionZ to ensure about animation takes precedence
      gsap.killTweensOf(this.camMovements, "positionZ")
      this.aboutAnimation.invalidate().restart()
    }

    if (state.pathname === "/" && prevState?.pathname !== "/") {
      this.isEntered = false
      // Kill any active tweens on positionZ to ensure intro animation takes precedence
      gsap.killTweensOf(this.camMovements, "positionZ")
      this.introAnimation.invalidate().restart()
    }

    // Project URL or other path: show loading (entered) position
    if (
      state.pathname !== "/" &&
      state.pathname !== "/about" &&
      prevState?.pathname !== state.pathname
    ) {
      this.isEntered = true
      gsap.killTweensOf(this.camMovements, "positionZ")
      this.introAnimation.invalidate().restart()
    }
  }

  removeCurtainAnimation() {
    const pathname = useBrowserLocation.getState().pathname

    if (pathname !== "/") {
      return
    }

    this.vertigoAnimation.invalidate()

    gsap.to(this.vertigoAnimation, {
      progress: 1,
      delay: 0.5,
      ease: "expor.inOut",
      duration: 5,
    })
  }

  createAnimation(
    key: "loading" | "loading-mobile" | "about" | "loading-exiting",
  ) {
    const camMov = cameraMovements.get(key)

    return gsap.to(this.camMovements, {
      paused: true,
      positionX: camMov.positionX,
      positionY: camMov.positionY,
      positionZ: camMov.positionZ,
      lookAtX: camMov.lookAtX,
      lookAtY: camMov.lookAtY,
      lookAtZ: camMov.lookAtZ,
      duration: 1,
      ease: "power2.inOut",
      overwrite: true, // Override any previous tweens on these properties
      onUpdate: () => {
        this.updateCameraPosition()
      },
    })
  }

  leaveAnimation(e: unknown) {
    const event = e as { progress: number }

    // We can use progress because the animation duration is 1 second
    this.introExitingAnimation.seek(event.progress)
  }

  resize() {
    super.resize()

    const pathname = useBrowserLocation.getState().pathname

    if (pathname === "/about") {
      return
    }

    if (this.isEntered) {
      const camMovLoading = cameraMovements.get(
        this.sizes.isMobile ? "loading-mobile" : "loading",
      )
      Object.assign(this.camMovements, camMovLoading)
    } else {
      const camMovLoading = cameraMovements.get(
        this.sizes.isMobile
          ? "loading-pre-vertigo-mobile"
          : "loading-pre-vertigo",
      )
      Object.assign(this.camMovements, camMovLoading)
    }
  }

  update() {
    super.update()

    if (this.isOrbitControls) {
      return
    }

    this.lerpPosition()
    this.calculateOffset()
    this.updateCameraPosition()
  }

  private updateCameraPosition() {
    // Apply mouse offset to camera position (flipped horizontally and vertically)
    const positionX =
      this.camMovements.positionX + (this.isEntered ? 0 : this.mouseOffset.x)
    const positionY = this.camMovements.positionY + -this.mouseOffset.y
    const positionZ =
      this.camMovements.positionZ + (this.isEntered ? this.mouseOffset.x : 0)

    // Apply mouse offset to look at position for rotation effect (flipped)
    const lookAtX =
      this.camMovements.lookAtX +
      (this.isEntered ? 0 : this.mouseOffset.x * 1.5)
    const lookAtY = this.camMovements.lookAtY + -this.mouseOffset.y * 1.5
    const lookAtZ =
      this.camMovements.lookAtZ + (this.isEntered ? this.mouseOffset.x : 0)

    this.camera.position.set(positionX, positionY, positionZ)
    this.camera.lookAt(lookAtX, lookAtY, lookAtZ)
  }

  private createVertigoAnimation() {
    const targetFov = cameraFovPostVertigo

    const initialZ = this.camMovements.positionZ
    const targetZ = cameraMovements.get(
      this.sizes.isMobile ? "loading-mobile" : "loading",
    ).positionZ

    const timeline = gsap.timeline({
      paused: true,
    })

    // Use .to() instead of .fromTo() to avoid setting initial FOV value
    // The initial FOV will be read from the camera's current value when animation plays
    timeline
      .to(
        this.camera,
        {
          fov: targetFov,
          duration: 1,
          ease: "power2.inOut",
          overwrite: true, // Override any previous tweens on fov
          onUpdate: () => {
            this.camera.updateProjectionMatrix()
          },
        },
        0,
      )
      .fromTo(
        this.camMovements,
        {
          positionZ: initialZ,
        },
        {
          positionZ: targetZ,
          duration: 1,
          ease: "power2.inOut",
          overwrite: true, // Override any previous tweens on positionZ
        },
        0,
      )

    return timeline
  }

  setDebug() {
    debugStore.sub(debugAtom, () => {
      const state = debugStore.get(debugAtom)

      this.isOrbitControls = state.showOrbitControls
    })

    if (this.debug.active && this.debug.ui) {
      const folder = this.debug.ui.addFolder({
        title: "📷 Camera",
        expanded: false,
      })

      const fovSettings = {
        fov: this.camera.fov,
      }

      folder
        .addBinding(fovSettings, "fov", {
          label: "FOV",
          min: 10,
          max: 120,
          step: 1,
        })
        .on("change", (ev) => {
          this.camera.fov = ev.value
          this.camera.updateProjectionMatrix()
        })

      const positionSettings = {
        z: this.camMovements.positionZ,
      }

      folder
        .addBinding(positionSettings, "z", {
          label: "Position Z",
          min: 3,
          max: 6,
          step: 0.01,
        })
        .on("change", (ev) => {
          this.camMovements.positionZ = ev.value
        })

      const vertigoProgress = {
        progress: 0,
      }

      folder
        .addBinding(vertigoProgress, "progress", {
          label: "Vertigo Effect",
          min: 0,
          max: 1,
          step: 0.001,
        })
        .on("change", (ev) => {
          this.vertigoAnimation.progress(ev.value)
        })
    }
  }
}
